import { explodePath, isInvisibleFile } from '@deepcloud/deep-ui-utils'

interface UploadEntriesOutputFile {
  file: File | null
  fileEntry?: FileSystemFileEntry
  idFolder?: string
}

interface UploadEntriesOutputFolder {
  name: string
  parent?: string
}

export interface UploadEntriesOutput {
  files: UploadEntriesOutputFile[]
  folders: Record<string, UploadEntriesOutputFolder>
  hasFolders: boolean
}

export function getAllFileEntriesFromButtonUpload(e: Event) {
  const target = e.target as HTMLInputElement
  const { files } = target

  if (files === null) return null

  const output: UploadEntriesOutput = {
    files: [],
    folders: {},
    hasFolders: false,
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    let levelFile = 0

    if (!isInvisibleFile(file?.name)) {
      const path = explodePath(file.webkitRelativePath)
      console.log(
        'getAllFileEntriesFromButtonUpload iterating ----',
        file.webkitRelativePath,
        path,
        path.length,
        file.name,
      )
      if (path.length > 0) {
        path.pop()
      }
      levelFile = path.length - 1
      path.forEach((nameFolder: string, level: number) => {
        let idFolder
        if (level > 0) {
          const levelFolderNames: string[] = []
          for (let a = 0; a < level + 1; a++) {
            levelFolderNames.push(path[a])
          }
          idFolder = `${level}-${levelFolderNames.join('-')}`
        } else {
          idFolder = `${level}-${nameFolder}`
        }
        let parentId
        if (level === 0) {
          parentId = null
        } else {
          const levelFolderNames: string[] = []
          for (let a = 0; a < level; a++) {
            levelFolderNames.push(path[a])
          }
          parentId = `${level - 1}-${levelFolderNames.join('-')}`
        }
        if (!output.folders[idFolder]) {
          output.hasFolders = true
          output.folders[idFolder] = {
            parent: parentId,
            name: nameFolder,
          }
        }
      })
      const levelFolderNames: string[] = []
      for (let a = 0; a < levelFile + 1; a++) {
        levelFolderNames.push(path[a])
      }
      let idFolder
      if (levelFolderNames.length > 0) {
        idFolder = `${levelFile}-${levelFolderNames.join('-')}`
      }
      //console.log("file", i, file.webkitRelativePath, file.name, path, folder)
      output.files.push({
        file,
        idFolder,
      })
    }
  }
  return output
}

// Wrap readEntries in a promise to make working with readEntries easier
function readEntriesPromise(
  directoryReader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    directoryReader.readEntries(resolve, reject)
  })
}

/** @public - exported for testing in utils.spec.ts */
export async function readAllDirectoryEntries(
  directoryReader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  const entries: FileSystemEntry[] = []

  while (true) {
    const readEntries = await readEntriesPromise(directoryReader)
    if (readEntries.length === 0) {
      break
    }
    entries.push(...readEntries)
  }

  return entries
}

// Converts a file entry into a File ready to be uploaded
function getFile(fileEntry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve, reject) => {
    if (fileEntry.isDirectory) {
      resolve(null)
    }

    fileEntry.file(resolve, reject)
  })
}

export function isUnhandleableDrop(e: DragEvent): boolean {
  const items = e.dataTransfer?.items
  if (!items) return false

  let hasFile = false
  let hasString = false

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.kind === 'file') hasFile = true
    if (item.kind === 'string') hasString = true
  }

  // Unhandleable if there are only strings and no actual files
  return hasString && !hasFile
}

export async function getAllFileEntries(
  event: DragEvent,
): Promise<UploadEntriesOutput[]> {
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) return []

  const dataTransferItemList = dataTransfer.items
  const outputItems: UploadEntriesOutput[] = []
  const queue: UploadEntriesOutputFile[] = []
  const entries: FileSystemEntry[] = []

  for (let i = 0; i < dataTransferItemList.length; i++) {
    const item = dataTransferItemList[i]
    const entry = item.webkitGetAsEntry()

    if (entry) {
      // Normal filesystem drop (files, folders)
      entries.push(entry)
    } else if (item.kind === 'file') {
      // Fallback for Outlook and other non-filesystem sources
      const file = item.getAsFile()
      if (file && !isInvisibleFile(file.name)) {
        outputItems.push({
          files: [{ file, fileEntry: undefined }],
          folders: {},
          hasFolders: false,
        })
      }
    }
  }

  const filePromises = entries.map((entry) =>
    getFile(entry as FileSystemFileEntry),
  )
  const files = await Promise.all(filePromises)

  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    queue.push({
      file,
      fileEntry: entries[i] as FileSystemFileEntry,
    })
  }

  async function addEntriesToExistingOutputEntry(
    dirEntries: FileSystemEntry[],
    outputEntry: UploadEntriesOutput,
    parentId: string,
  ): Promise<void> {
    for (const dirEntry of dirEntries) {
      const dirEntryFullPath = explodePath(dirEntry.fullPath)
      const dirEntryLevel = dirEntryFullPath.length
      const dirEntryIdFolder = `${dirEntryLevel - 1}-${dirEntryFullPath.join('-')}`

      if (dirEntry.isDirectory) {
        outputEntry.folders[dirEntryIdFolder] = {
          parent: parentId,
          name: dirEntry.name,
        }

        const reader = (dirEntry as FileSystemDirectoryEntry).createReader()
        const childrenEntries = await readAllDirectoryEntries(reader)

        if (childrenEntries.length > 0) {
          await addEntriesToExistingOutputEntry(
            childrenEntries,
            outputEntry,
            dirEntryIdFolder,
          )
        }
      } else if (dirEntry.isFile && !isInvisibleFile(dirEntry.name)) {
        const file = await getFile(dirEntry as FileSystemFileEntry)

        outputEntry.files.push({
          file,
          fileEntry: dirEntry as FileSystemFileEntry,
          idFolder: parentId,
        })
      }
    }
  }

  async function addUploadEntryOutput(
    entry: FileSystemEntry | undefined,
  ): Promise<void> {
    if (!entry) return

    const fullPath = explodePath(entry.fullPath)
    const level = fullPath.length
    const isSingleEntry = level === 1

    let folderId: string | undefined
    if (entry.isDirectory && !isInvisibleFile(entry.name)) {
      folderId = `${level}-${fullPath.join('-')}`
    }

    if (entry.isFile && !isInvisibleFile(entry.name)) {
      if (isSingleEntry) {
        const file = await getFile(entry as FileSystemFileEntry)

        outputItems.push({
          files: [
            {
              file,
              fileEntry: entry as FileSystemFileEntry,
            },
          ],
          folders: {},
          hasFolders: false,
        })

        return
      }
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      const dirEntries = await readAllDirectoryEntries(reader)

      if (dirEntries.length === 0 && level === 1) {
        outputItems.push({
          files: [],
          folders: {
            [folderId!]: {
              parent: undefined,
              name: entry.name,
            },
          },
          hasFolders: true,
        })
        return
      }

      const outputEntry: UploadEntriesOutput = {
        files: [],
        folders: {
          [folderId!]: {
            parent: undefined,
            name: entry.name,
          },
        },
        hasFolders: true,
      }

      await addEntriesToExistingOutputEntry(dirEntries, outputEntry, folderId!)
      outputItems.push(outputEntry)
    }
  }

  while (queue.length > 0) {
    const entry = queue.shift()
    if (entry) {
      await addUploadEntryOutput(entry.fileEntry)
    }
  }

  return outputItems
}
